import React from 'react';
import { cn } from '../../../../lib/utils';
import { KPI_FORMAT_OPTIONS, KPI_SECTION_COLORS } from '../../utils/kpiItems';
import { GAUGE_VARIANTS } from '../../components/widgets/GaugeWidget';

const inputCls = 'w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-200';
const selectCls = inputCls;

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-0.5">
      {label && <label className="text-[10px] font-semibold text-slate-500">{label}</label>}
      {children}
    </div>
  );
}

function FormulaField({ label, value, onChange, fieldOptions, placeholder }) {
  return (
    <Field label={label}>
      <input
        className={cn(inputCls, 'font-mono dir-ltr text-left')}
        dir="ltr"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || ''}
      />
      {fieldOptions?.length > 0 && (
        <select
          className={cn(selectCls, 'mt-1')}
          value=""
          onChange={(e) => {
            const key = e.target.value;
            if (!key) return;
            const next = value && String(value).trim() ? `${value} ${key}` : key;
            onChange(next);
          }}
        >
          <option value="">+ درج فیلد کوئری</option>
          {fieldOptions.map((f) => (
            <option key={f.key || f} value={f.key || f}>
              {f.fa ? `${f.fa} (${f.key})` : (f.key || f)}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}

function ColorChips({ value, onChange }) {
  return (
    <div className="flex flex-wrap gap-1">
      {KPI_SECTION_COLORS.map((c) => (
        <button
          key={c.value || 'auto'}
          type="button"
          title={c.label}
          onClick={() => onChange(c.value)}
          className={cn(
            'h-6 min-w-[1.5rem] rounded-full border px-1.5 text-[9px]',
            (value || '') === c.value ? 'border-indigo-400 ring-1 ring-indigo-200' : 'border-slate-200',
          )}
          style={c.value ? { backgroundColor: c.value, color: '#fff' } : undefined}
        >
          {c.value ? '' : c.label}
        </button>
      ))}
      <input
        type="color"
        value={value || '#4f46e5'}
        onChange={(e) => onChange(e.target.value)}
        className="h-6 w-7 cursor-pointer rounded border border-slate-200"
      />
    </div>
  );
}

function KeySelect({ label, value, onChange, fieldOptions }) {
  return (
    <Field label={label}>
      <select className={selectCls} value={value || ''} onChange={(e) => onChange(e.target.value)}>
        <option value="">— بدون ستون —</option>
        {fieldOptions.map((f) => (
          <option key={f.key || f} value={f.key || f}>
            {f.fa ? `${f.fa} (${f.key})` : (f.key || f)}
          </option>
        ))}
      </select>
    </Field>
  );
}

export default function GaugeEditor({ widget, fieldOptions = [], onChange }) {
  const isProgress = widget.type === 'progress_bar';
  const cfg = isProgress
    ? (widget.progressConfig || widget.gaugeConfig || {})
    : (widget.gaugeConfig || {});
  const fields = fieldOptions.map((f) => (typeof f === 'string' ? { key: f, fa: f } : f));

  const patch = (p) => {
    const next = { ...cfg, ...p };
    if (isProgress) onChange({ progressConfig: next });
    else onChange({ gaugeConfig: next });
  };

  const variants = isProgress
    ? GAUGE_VARIANTS.filter((v) => v.value === 'linear' || v.value === 'steps' || v.value === 'bullet')
    : GAUGE_VARIANTS;

  return (
    <div className="space-y-3">
      <Field label="شکل نمایش">
        <div className="grid grid-cols-2 gap-1.5">
          {variants.map((v) => (
            <button
              key={v.value}
              type="button"
              onClick={() => patch({ variant: v.value })}
              className={cn(
                'rounded-lg border px-2 py-2 text-[11px] font-bold transition-colors',
                (cfg.variant || (isProgress ? 'linear' : 'arc')) === v.value
                  ? 'border-indigo-400 bg-indigo-50 text-indigo-700'
                  : 'border-slate-200 text-slate-600 hover:bg-slate-50',
              )}
            >
              <span className="ml-1">{v.icon}</span>
              {v.label}
            </button>
          ))}
        </div>
      </Field>

      <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-2.5 space-y-2">
        <p className="text-[10px] font-bold text-indigo-700">مقدار واقعی</p>
        <FormulaField
          label="فرمول"
          value={cfg.actualFormula || cfg.valueFormula || ''}
          onChange={(actualFormula) => patch({ actualFormula, valueFormula: actualFormula })}
          fieldOptions={fields}
          placeholder="sale"
        />
        <KeySelect
          label="یا ستون"
          value={cfg.actualKey || cfg.valueKey || ''}
          onChange={(actualKey) => patch({ actualKey, valueKey: actualKey })}
          fieldOptions={fields}
        />
      </div>

      <div className="rounded-xl border border-slate-100 p-2.5 space-y-2">
        <p className="text-[10px] font-bold text-slate-500">هدف</p>
        <FormulaField
          label="فرمول هدف"
          value={cfg.targetFormula || cfg.maxFormula || ''}
          onChange={(targetFormula) => patch({ targetFormula, maxFormula: targetFormula })}
          fieldOptions={fields}
          placeholder="target"
        />
        <KeySelect
          label="یا ستون"
          value={cfg.targetKey || cfg.maxKey || ''}
          onChange={(targetKey) => patch({ targetKey, maxKey: targetKey })}
          fieldOptions={fields}
        />
      </div>

      <FormulaField
        label="فرمول درصد پیشرفت (اختیاری)"
        value={cfg.progressFormula || ''}
        onChange={(progressFormula) => patch({ progressFormula })}
        fieldOptions={fields}
        placeholder="(sale / target) * 100"
      />

      <div className="grid grid-cols-2 gap-2">
        <Field label="نوع نمایش">
          <select
            className={selectCls}
            value={cfg.format || (isProgress ? 'percent' : 'number')}
            onChange={(e) => patch({ format: e.target.value })}
          >
            {KPI_FORMAT_OPTIONS.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </Field>
        <Field label="اعشار">
          <input
            type="number"
            min={0}
            max={6}
            className={inputCls}
            value={cfg.decimals ?? 0}
            onChange={(e) => patch({ decimals: Number(e.target.value) })}
          />
        </Field>
      </div>

      <Field label="رنگ">
        <ColorChips value={cfg.color || ''} onChange={(color) => patch({ color })} />
      </Field>

      {!isProgress && (
        <Field label="اندازه گیج">
          <input
            type="range"
            min={120}
            max={280}
            step={10}
            value={Number(cfg.size) || 200}
            onChange={(e) => patch({ size: Number(e.target.value) })}
          />
        </Field>
      )}

      <Field label="برچسب کمکی">
        <input
          className={inputCls}
          value={cfg.subLabel || ''}
          onChange={(e) => patch({ subLabel: e.target.value })}
          placeholder="هدف ماه"
        />
      </Field>

      <label className="flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer">
        <input
          type="checkbox"
          className="accent-indigo-600"
          checked={cfg.showValue !== false}
          onChange={(e) => patch({ showValue: e.target.checked })}
        />
        نمایش مقدار
      </label>
      <label className="flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer">
        <input
          type="checkbox"
          className="accent-indigo-600"
          checked={cfg.showTarget !== false}
          onChange={(e) => patch({ showTarget: e.target.checked })}
        />
        نمایش هدف
      </label>
    </div>
  );
}
